import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Users, User, ExternalLink, Star, Loader2, Target, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { getAccessStatus, trackAnalyticsEvent, API_URL } from '../lib/api';
import { checkAndTrackAffiliateReferral, getAffiliateReferralCode, clearAffiliateReferralCode } from '../utils/affiliateTracking';
import { getUTMForAPI, clearUTMParams } from '../utils/utmTracking';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [signupRole, setSignupRole] = useState('trainee');
  const [affiliateCode, setAffiliateCode] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    traineeCode: '',
    playerGroup: '',
  });
  
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  
  // Check for affiliate referral on page load
  useEffect(() => {
    const initAffiliateTracking = async () => {
      await checkAndTrackAffiliateReferral();
      const code = getAffiliateReferralCode();
      if (code) {
        setAffiliateCode(code);
      }
    };
    initAffiliateTracking();
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        const user = await login(formData.email, formData.password);
        
        // Simplified login flow - just redirect based on role
        toast.success('Welcome back!');
        navigate(user.role === 'coach' ? '/coach' : '/dashboard');
      } else {
        const isTrainee = signupRole === 'trainee';
        const isDigitalLab = signupRole === 'digital_lab';
        const isRealSkillApp = signupRole === 'realskill_app';
        const actualRole = signupRole === 'coach' ? 'coach' : 'player';
        
        // Get UTM parameters for attribution
        const utmParams = getUTMForAPI();
        
        const registerData = {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          role: actualRole,
          is_trainee: isTrainee,
          is_digital_lab: isDigitalLab || isRealSkillApp,
          player_group: (actualRole === 'player' || isDigitalLab || isRealSkillApp) ? (formData.playerGroup || 'online') : null,
          // UTM tracking data
          ...utmParams
        };
        
        if (isTrainee) {
          registerData.trainee_code = formData.traineeCode;
        }
        
        // Include affiliate referral code if present
        if (affiliateCode) {
          registerData.affiliate_code = affiliateCode;
        }
        
        const res = await axios.post(`${API_URL}/auth/register`, registerData);
        
        const token = res.data.access_token;
        const user = res.data.user;
        
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(user);
        
        // Track signup event for analytics with UTM data
        trackAnalyticsEvent('signup', '/auth', signupRole, {
          role: actualRole,
          is_trainee: isTrainee,
          is_digital_lab: isDigitalLab,
          is_realskill_app: isRealSkillApp,
          has_affiliate: !!affiliateCode,
          ...utmParams
        });
        
        // Clear affiliate cookie after successful registration
        if (affiliateCode) {
          clearAffiliateReferralCode();
        }
        
        // Clear UTM params after successful conversion
        clearUTMParams();
        
        if (isTrainee) {
          toast.success('Welcome! Let\'s get you set up.');
          navigate('/onboarding');
        } else if (isDigitalLab) {
          // Digital Lab signup - go directly to Stripe checkout
          toast.info('Creating your account... Redirecting to payment.');
          try {
            const originUrl = window.location.origin;
            const checkoutRes = await axios.post(`${API_URL}/payments/create-checkout`, {
              plan_type: 'digital_lab',
              origin_url: originUrl,
              with_trial: false  // No trial for Digital Lab - immediate access after payment
            });
            // Redirect directly to Stripe checkout
            window.location.href = checkoutRes.data.checkout_url;
          } catch (checkoutError) {
            console.error('Checkout error:', checkoutError);
            toast.error('Failed to start checkout. Please try from the payment page.');
            navigate('/payment?plan=digital_lab');
          }
        } else if (isRealSkillApp) {
          // RealSkill App signup - go directly to Stripe checkout
          toast.info('Creating your account... Redirecting to payment.');
          try {
            const originUrl = window.location.origin;
            const checkoutRes = await axios.post(`${API_URL}/payments/create-checkout`, {
              plan_type: 'realskill_app',
              origin_url: originUrl,
              with_trial: false  // No trial for RealSkill App
            });
            // Redirect directly to Stripe checkout
            window.location.href = checkoutRes.data.checkout_url;
          } catch (checkoutError) {
            console.error('Checkout error:', checkoutError);
            toast.error('Failed to start checkout. Please try from the payment page.');
            navigate('/realskill-app');
          }
        } else if (actualRole === 'coach') {
          toast.success('Account created! Let\'s get you set up.');
          navigate('/onboarding');
        } else {
          toast.success('Account created! Please subscribe to access the training content.');
          navigate('/payment');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      const message = error.response?.data?.detail || error.message || 'Something went wrong. Please try again.';
      
      if (message.includes('already registered')) {
        toast.error('Email already registered. Please sign in instead.');
        setIsLogin(true);
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex bg-[#050505]">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-10">
            <div className="flex flex-col">
              <span className="font-heading font-black text-xl tracking-tight text-white uppercase">Chris Jordan's</span>
              <span className="font-heading text-sm tracking-wider text-zinc-400 uppercase">Skill Lab</span>
            </div>
          </div>
          
          {/* Title */}
          <h1 className="heading-xl text-white mb-2">
            {isLogin ? 'Welcome Back' : 'Train Different.'}
          </h1>
          <p className="text-zinc-400 mb-8">
            {isLogin ? 'Sign in to continue your training' : 'Create your account. Track progress. Prove everything.'}
          </p>
          
          {/* Role Toggle for Signup */}
          {!isLogin && (
            <div className="mb-8">
              <Label className="label-caps text-zinc-500 mb-3 block">I am a...</Label>
              
              {/* Digital Lab Featured Banner */}
              <div 
                onClick={() => setSignupRole('digital_lab')}
                className={`mb-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  signupRole === 'digital_lab'
                    ? 'bg-gradient-to-r from-white/10 to-zinc-500/10 border-white'
                    : 'bg-gradient-to-r from-[#0a0a0a] to-[#111] border-[#27272a] hover:border-white/50'
                }`}
                data-testid="role-digital-lab-btn"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    signupRole === 'digital_lab' ? 'bg-white' : 'bg-white/20'
                  }`}>
                    <Target size={28} className={signupRole === 'digital_lab' ? 'text-black' : 'text-white'} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-black text-white uppercase tracking-wide">The Digital Lab</span>
                      <span className="px-2 py-0.5 bg-white text-black text-[10px] font-bold rounded uppercase">Complete</span>
                    </div>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-2xl font-black text-white">$150</span>
                      <span className="text-zinc-500 text-sm">/mo</span>
                    </div>
                    <ul className="space-y-1 text-xs text-zinc-400">
                      <li className="flex items-center gap-2"><Sparkles size={12} className="text-white" /> Full Court IQ App Access</li>
                      <li className="flex items-center gap-2"><Sparkles size={12} className="text-white" /> I'm Possible Pro-Drill Library (RealSkill App)</li>
                      <li className="flex items-center gap-2"><Sparkles size={12} className="text-white" /> Private Skool Community Access</li>
                      <li className="flex items-center gap-2"><Sparkles size={12} className="text-white" /> Global Leaderboard Ranking</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* RealSkill App Only Option */}
              <div
                onClick={() => setSignupRole('realskill_app')}
                className={`relative p-5 rounded-xl border cursor-pointer transition-all ${
                  signupRole === 'realskill_app' 
                    ? 'bg-purple-500/10 border-purple-500' 
                    : 'bg-gradient-to-r from-[#0a0a0a] to-[#111] border-[#27272a] hover:border-purple-500/50'
                }`}
                data-testid="role-realskill-btn"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    signupRole === 'realskill_app' ? 'bg-purple-500' : 'bg-purple-500/20'
                  }`}>
                    <Target size={28} className={signupRole === 'realskill_app' ? 'text-white' : 'text-purple-400'} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-black text-white uppercase tracking-wide">RealSkill App</span>
                      <span className="px-2 py-0.5 bg-purple-500 text-white text-[10px] font-bold rounded uppercase">Drills Only</span>
                    </div>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-2xl font-black text-purple-400">$49.99</span>
                      <span className="text-zinc-500 text-sm">/mo</span>
                    </div>
                    <ul className="space-y-1 text-xs text-zinc-400">
                      <li className="flex items-center gap-2"><Sparkles size={12} className="text-purple-400" /> I'm Possible Pro-Drill Library</li>
                      <li className="flex items-center gap-2"><Sparkles size={12} className="text-purple-400" /> 100+ Training Videos</li>
                      <li className="flex items-center gap-2"><Sparkles size={12} className="text-purple-400" /> Follow-Along Workouts</li>
                      <li className="flex items-center gap-2"><Sparkles size={12} className="text-purple-400" /> Mobile App Access</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                {/* Trainee Option */}
                <button
                  type="button"
                  onClick={() => setSignupRole('trainee')}
                  className={`p-4 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                    signupRole === 'trainee' 
                      ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' 
                      : 'bg-[#0a0a0a] border-[#27272a] text-zinc-400 hover:border-zinc-600'
                  }`}
                  data-testid="role-trainee-btn"
                >
                  <Star size={20} />
                  <span className="font-bold text-xs uppercase">Trainee</span>
                  <span className="text-[10px] opacity-70">FREE</span>
                </button>
                
                {/* Player Option */}
                <button
                  type="button"
                  onClick={() => setSignupRole('player')}
                  className={`p-4 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                    signupRole === 'player' 
                      ? 'bg-white/20 border-white text-white' 
                      : 'bg-[#0a0a0a] border-[#27272a] text-zinc-400 hover:border-zinc-600'
                  }`}
                  data-testid="role-player-btn"
                >
                  <User size={20} />
                  <span className="font-bold text-xs uppercase">Player</span>
                  <span className="text-[10px] opacity-70">$20/mo</span>
                </button>
                
                {/* Coach / Parent Option */}
                <button
                  type="button"
                  onClick={() => setSignupRole('coach')}
                  className={`p-4 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                    signupRole === 'coach' 
                      ? 'bg-purple-500/20 border-purple-500 text-purple-400' 
                      : 'bg-[#0a0a0a] border-[#27272a] text-zinc-400 hover:border-zinc-600'
                  }`}
                  data-testid="role-coach-btn"
                  title="Select this to monitor your athlete's progress"
                >
                  <Users size={20} />
                  <span className="font-bold text-xs uppercase">Coach</span>
                  <span className="text-[10px] opacity-70">$28/mo</span>
                </button>
              </div>
              
              {/* Role Description */}
              <div className="mt-4 p-4 bg-[#0a0a0a] border border-[#27272a] rounded-lg text-sm">
                {signupRole === 'digital_lab' && (
                  <p className="text-white">
                    <strong>The Digital Lab (Complete Package)</strong> - Full Court IQ App + RealSkill Drills + Community Access + Shot Tracking. Best value for serious players.
                  </p>
                )}
                {signupRole === 'realskill_app' && (
                  <p className="text-purple-400">
                    <strong>RealSkill App (Drills Only)</strong> - Access to Micah Lancaster's I'm Possible Pro-Drill Library with 100+ training videos. Perfect if you just want the drills.
                  </p>
                )}
                {signupRole === 'trainee' && (
                  <p className="text-emerald-400">
                    <strong>Train with Coach Chris Jordan</strong> - If you're one of my trainees, select this for FREE access to all training content.
                  </p>
                )}
                {signupRole === 'player' && (
                  <p className="text-white">
                    <strong>Independent Player</strong> - Full access to training content for $20/month.
                  </p>
                )}
                {signupRole === 'coach' && (
                  <p className="text-purple-400">
                    <strong>Coach / Parent</strong> - $28/month includes 2 player slots. Monitor your athlete's progress and training.
                  </p>
                )}
              </div>
              
              {/* Trainee Code Input */}
              {signupRole === 'trainee' && (
                <div className="mt-4">
                  <Label className="label-caps text-emerald-400">Trainee Code *</Label>
                  <Input
                    type="text"
                    value={formData.traineeCode}
                    onChange={(e) => setFormData({...formData, traineeCode: e.target.value})}
                    className="input-pro mt-2 border-emerald-500/50 focus:border-emerald-500"
                    placeholder="Enter your trainee code"
                    required={signupRole === 'trainee'}
                    data-testid="trainee-code-input"
                  />
                  <p className="text-xs text-zinc-500 mt-2">
                    Ask your coach for the trainee code
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <Label className="label-caps text-zinc-400">Full Name</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input-pro mt-2"
                  placeholder="Enter your name"
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}
            
            {/* Player Group Selection */}
            {!isLogin && signupRole !== 'coach' && (
              <div>
                <Label className="label-caps text-zinc-400">Player Group (optional)</Label>
                <select
                  value={formData.playerGroup}
                  onChange={(e) => setFormData({...formData, playerGroup: e.target.value})}
                  className="input-pro mt-2 w-full"
                  data-testid="player-group-select"
                >
                  <option value="">Select group (optional)</option>
                  <option value="girls">Girls</option>
                  <option value="group_a_boys">Group A Boys</option>
                  <option value="group_b_boys">Group B Boys</option>
                  <option value="online">Online Players Only</option>
                </select>
                <p className="text-xs text-zinc-500 mt-2">
                  Select your group for ranked leaderboards
                </p>
              </div>
            )}
            
            <div>
              <Label className="label-caps text-zinc-400">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="input-pro mt-2"
                placeholder="Enter your email"
                required
                data-testid="email-input"
              />
            </div>
            
            <div>
              <Label className="label-caps text-zinc-400">Password</Label>
              <div className="relative mt-2">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="input-pro pr-12"
                  placeholder="Enter your password"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              style={{ backgroundColor: 'white', color: 'black' }}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin mr-2" />
              ) : null}
              {isLogin ? 'Sign In' : 'Create Account'}
              <ArrowRight size={18} className="ml-2" />
            </Button>
            
            {/* Forgot Password Link */}
            {isLogin && (
              <div className="mt-4 text-center">
                <Link to="/reset-password" className="text-sm text-zinc-500 hover:text-white">
                  Forgot your password?
                </Link>
              </div>
            )}
          </form>
          
          {/* Toggle Login/Register */}
          <div className="mt-8 text-center">
            <p className="text-zinc-500">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setFormData({ name: '', email: formData.email, password: '', traineeCode: '', playerGroup: '' });
                }}
                className="ml-2 text-white hover:underline font-semibold"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
          
          {/* Community Link */}
          <div className="mt-8 pt-8 border-t border-[#27272a]">
            <a
              href="https://www.skool.com/skill-domination-x-4472"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-amber-400 hover:text-amber-300 transition-colors text-sm"
            >
              <ExternalLink size={16} />
              Join our free community on Skool
            </a>
          </div>
        </div>
      </div>
      
      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-transparent to-[#050505]/80 z-10" />
        <img 
          src="https://images.unsplash.com/photo-1519432473078-0151c4f90335?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Basketball training"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-12">
          <h2 className="heading-xl text-white mb-4">
            SILENT<br />
            <span className="text-zinc-400">DOMINANCE</span>
          </h2>
          <p className="text-zinc-300 text-lg max-w-md">
            Work in silence. Let your game speak. Track every shot, master every move, dominate every court.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
